<template>
  <v-row align="center" justify="center" ref="widget">
    <!-- loader -->
    <template v-if="loaderMain">
      <v-progress-circular class="v-progress-circular-fixed" indeterminate color="secondary" />
    </template>
    <!-- end loader -->

    <!-- error -->
    <template v-else-if="error">
      <v-col cols="12" class="pa-8 text-center">

        <template v-if="error === 'session_failed'">
          <v-icon color="red darken-1" large>mdi-alert</v-icon>
          <span class="d-block body-1 font-weight-medium mt-4">Piriod widget installation is failed</span>
          <span class="d-block body-2">Session property is missing in window.piriodSettings</span>
        </template>

        <template v-else-if="error === 'not_allowed'">
          <v-icon color="red darken-1" large>mdi-alert</v-icon>
          <span class="d-block body-1 font-weight-medium mt-4">Piriod widget installation is failed</span>
          <span class="d-block body-2">Email authentication is not allowed. Send session key throught window.piriodSettings</span>
        </template>

        <template v-else-if="error === 'session_expired'">
          <v-icon color="indigo" large>mdi-clock</v-icon>
          <span class="d-block body-1 font-weight-medium mt-4">{{$t('cb.expiredSessionMessage')}}</span>
          <v-btn class="mt-10" color="indigo" dark :href="session.return_url || base.return_url">Volver</v-btn>
        </template>

        <template v-else-if="error === 'base_is_not_exist'">
          <v-icon color="red darken-1" large>mdi-alert</v-icon>
          <span class="d-block body-1 font-weight-medium mt-4">{{$t('cb.pageIsNotExistsMessage')}}</span>
          <span class="d-block body-2">Verifica la URL que te trajo hasta acá</span>
        </template>

        <template v-else-if="error === 'session_is_not_exist'">
          <v-icon color="red darken-1" large>mdi-alert</v-icon>
          <span class="d-block body-1 font-weight-medium mt-4">{{$t('cb.pageIsNotExistsMessage')}}</span>
        </template>

      </v-col>
    </template>
    <!-- end error -->

    <template v-else>
      <!-- signin -->
      <v-card class="my-16" v-if="$route.name === 'CustomerBaseAuth'" width="400" flat>

        <!-- auth error -->
        <v-alert class="mx-3" type="error" dense v-if="authError">{{authError}}</v-alert>
        <!-- end auth error -->

        <template v-if="authEmailSended">
          <v-sheet class="py-6 px-3 text-center">
            <v-avatar color="primary" size="36px">
              <v-icon color="white">mdi-check</v-icon>
            </v-avatar>
            <span class="d-block mt-4 body-1 text--secondary font-weight-medium">{{$t('cb.successAccessMessage')}}</span>
          </v-sheet>
        </template>
        <template v-else>
          <v-card-text class="py-4 px-3">
            <span class="d-block mb-3 body-1 font-weight-medium">{{$t('cb.email')}}</span>
            <v-text-field v-model.trim="$v.auth.email.$model" :error="$v.auth.email.$error" placeholder="example@email.com" background-color="white" height="36" outlined required single-line dense hide-details />
          </v-card-text>
          <v-divider class="mx-3" />
          <v-card-actions class="py-4 px-3">
            <v-btn @click="signin" color="primary" :loading="loaderAuthEmail" block>{{$t('cb.getAccessAction')}}</v-btn>
          </v-card-actions>
        </template>
      </v-card>
      <!-- end signin -->

      <!-- content -->
      <v-col cols="12" v-if="$route.name === 'CustomerBase'">
        <v-card :color="options.backgroundColor.value" flat>

          <!-- billing information -->
          <v-card-text>
            <v-row no-gutters>
              <span class="my-2 subtitle-1 font-weight-medium">{{$t('cb.billingInformation')}}</span>
              <v-sheet outlined rounded width="100%">
                <v-list class="py-3 transparent">
                  <v-list-item>
                    <v-list-item-title class="body-2">
                      <span class="d-block text--secondary font-weight-medium">{{billing.name}}</span>
                      <span class="d-block">{{billing.tax_id}}</span>
                      <span class="d-block">{{billing.address}} - {{billing.state}}, {{billing.country}}</span>
                      <span class="d-block">{{billing.email}}</span>
                      <span class="d-block" v-if="billing.phone">{{billing.phone}}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle class="text-right" v-if="base.manage_billing">
                      <v-btn @click="dialogBilling=true" outlined>{{$t('cb.edit')}}</v-btn>
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-sheet>
            </v-row>
          </v-card-text>

          <v-dialog v-model="dialogBilling" width="500" persistent no-click-animation scrollable>
            <v-card>
              <v-card-title class="pa-0">
                <v-toolbar flat height="55">
                  <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">{{$t('cb.editBillingInformation')}}</v-toolbar-title>
                  <v-spacer />
                  <v-toolbar-items>
                    <v-row align="center" justify="center" class="mr-1">
                      <v-btn class="ml-3" @click="dialogBilling=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
                    </v-row>
                  </v-toolbar-items>
                </v-toolbar>
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-0 background">
                <v-list class="py-4">
                  <v-list-item>
                    <v-list-item-title>{{$t('cb.name')}}</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-text-field v-model.trim="$v.billing.name.$model" :error="$v.billing.name.$error" background-color="white" outlined required single-line dense placeholder="Acme LLC" maxlength="100" hide-details />
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>{{$t('cb.address')}}</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-text-field v-model.trim="$v.billing.address.$model" :error="$v.billing.address.$error" background-color="white" outlined required single-line dense maxlength="64" hide-details />
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>{{$t('cb.state')}}</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-select v-model="$v.billing.state.$model" :error="$v.billing.state.$error" :items="statesList" item-text="name" item-value="id" single-line hide-details dense height="30" outlined background-color="white" no-data-text="Sin estados disponibles." />
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="mt-3">
                    <v-list-item-title>
                      <span class="text--secondary font-weight-medium">{{$t('cb.fiscalInformation')}}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="billing.country === 'CL'">
                    <v-list-item-title>{{$t('cb.businessActivity')}}</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-text-field v-model.trim="$v.billing.tax_settings.cl_activity_description.$model" :error="$v.billing.tax_settings.cl_activity_description.$error" background-color="white" outlined required single-line dense maxlength="64" hide-details />
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="mt-3">
                    <v-list-item-title>
                      <span class="text--secondary font-weight-medium">{{$t('cb.contactInformation')}}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>{{$t('cb.billingEmail')}}</v-list-item-title>
                    <v-list-item-subtitle>
                    <v-text-field v-model.trim="$v.billing.email.$model" :error="$v.billing.email.$error" outlined required single-line background-color="white" dense maxlength="64" hide-details />
                    </v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-title>{{$t('cb.phone')}}</v-list-item-title>
                    <v-list-item-subtitle>
                      <v-text-field v-model.trim="$v.billing.phone.$model" :error="$v.billing.phone.$error" background-color="white" outlined required single-line dense v-if="billing.country" maxlength="16" hide-details />
                    </v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-card-text>
              <v-divider />
              <v-card-actions class="pa-4">
                <v-spacer />
                <v-btn outlined @click="dialogBilling=false">{{$t('cb.cancel')}}</v-btn>
                <v-btn @click="patchCustomer" :color="base.color_background" dark>{{$t('cb.save')}}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- end billing information -->

          <!-- subscriptions -->
          <v-card-text v-if="subscriptionsList.length">
            <template v-for="(s, i) in subscriptionsList">
              <v-row :class="{ 'mt-6': (i !== 0) }" no-gutters :key="`${s.id}-title`">
                <v-col cols="12" class="pb-2">
                  <span class="subtitle-1 font-weight-medium">{{$t('cb.subscription')}}</span>
                  <v-chip class="mt-n1 ml-1" v-if="s.status === 'active'" color="blue accent-4" text-color="white" small>{{$t('cb.active')}}</v-chip>
                  <v-chip class="mt-n1 ml-1" v-else-if="s.status === 'paused'" color="blue-grey darken-1" text-color="white" small>{{$t('cb.paused')}}</v-chip>
                  <v-chip class="mt-n1 ml-1" v-else-if="s.status === 'cancelled'" color="blue-grey darken-1" text-color="white" small>{{$t('cb.cancelled')}}</v-chip>
                </v-col>
              </v-row>
              <v-card outlined :key="`${s.id}-content`">
                <v-card-text class="background-resumen">
                  <v-row align="center">
                    <v-col cols="3">
                      <span class="d-block mb-1 text--primary">{{$t('cb.startAt')}}</span>
                      <span class="body-1 font-weight-medium mr-1">{{s.date_start | date}}</span>
                    </v-col>
                    <template v-if="s.status === 'active'">
                      <template v-if="s.end_date">
                        <v-divider class="my-2" vertical />
                        <v-col cols="3">
                          <span class="d-block mb-1 text--primary">{{$t('cb.endsAt')}}</span>
                          <span class="body-1 font-weight-medium mr-1">{{s.end_date | date}}</span>
                        </v-col>
                      </template>
                      <v-divider class="my-2" vertical />
                      <v-col cols="3">
                        <span class="d-block mb-1 text--primary">{{$t('cb.nextBilling')}}</span>
                        <span class="body-1 font-weight-medium mr-1">{{s.next_billing | date}}</span>
                      </v-col>
                      <template v-if="s.coupon">
                        <v-divider class="my-2" vertical />
                        <v-col cols="3">
                          <span class="d-block mb-1 text--primary">{{$t('cb.discount')}}</span>
                          <span class="d-block mb-1">
                            <span class="body-1 font-weight-medium mr-1">
                              <template v-if="s.coupon.discount_scheme === '%'">{{s.coupon.amount | number(false)}} %</template>
                              <template v-else-if="s.coupon.discount_scheme === '$'">{{s.coupon.amount | currency(s.coupon.currency.id)}} {{s.coupon.currency.id}}</template>
                            </span>

                            <!-- coupon life -->
                            <template v-if="s.coupon.duration_scheme === 'one_time'">por {{s.coupon_redemptions ? 0 : 1}} ciclo(s) de facturación restantes</template>
                            <template v-else-if="s.coupon.duration_scheme === 'forever'">para siempre</template>
                            <template v-else-if="s.coupon.duration_scheme === 'limited'">por {{s.coupon.duration_times - s.coupon_redemptions}} ciclos de facturación restantes</template>
                            <!-- end coupon life -->
                          </span>
                        </v-col>
                      </template>
                    </template>
                    <v-col class="text-right" v-if="base.cancel_subscription && ['active', 'paused'].includes(s.status)">
                      <!-- cancel subscription menu -->
                      <v-menu min-width="160px" close-on-click close-on-content-click offset-y left :nudge-bottom="10" transition="slide-y-transition">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn v-bind="attrs" v-on="on" outlined>{{$t('cb.cancelSubscription')}}</v-btn>
                        </template>
                        <v-card>
                          <v-card-title class="py-2">
                            <span class="text--secondary subtitle-1 font-weight-medium">{{$t('cb.cancelSubscription')}}</span>
                          </v-card-title>
                          <v-divider />
                          <v-card-text>
                            <span class="d-block mb-4">{{ $t('cb.continueWarningMessage') }}</span>
                            <span class="d-block font-weight-medium">{{ $t('cb.undoneMessage') }}</span>
                          </v-card-text>
                          <v-divider />
                          <v-card-actions class="px-3">
                            <v-spacer />
                            <v-btn @click="cancelSubscription(s)" color="red" dark :loading="loaderCancelSubscription">{{ $t('cb.confirm') }}</v-btn>
                          </v-card-actions>
                        </v-card>
                      </v-menu>
                      <!-- end cancel subscription menu -->
                    </v-col>
                  </v-row>
                </v-card-text>
                <v-divider />
                <v-card-text>
                  <v-row class="py-3" no-gutters align="center" justify="space-between" v-for="(line, i) in s.lines" :key="`${i}-line`">
                    <v-col cols="6">
                      <span class="d-block body-2 font-weight-medium mr-1">
                        {{line.plan.product}} • {{line.plan.name}}
                        <v-tooltip v-if="line.plan.usage_scheme === 'metered'" right max-width="280px" transition="scale-transition">
                          <template v-slot:activator="{ on }">
                            <v-icon class="ml-1" v-on="on" small :color="base.color_background">mdi-timelapse</v-icon>
                          </template>
                          <span>{{$t('cb.useMessage')}}</span>
                        </v-tooltip>
                      </span>
                      <span class="d-block text--primary subtitle mr-1">
                        <template v-if="line.plan.tiers.length">{{$t('cb.from')}}</template>
                        {{line.plan.amount | currency(line.plan.currency.id)}} {{line.plan.currency.id}} {{$t('cb.by')}} {{line.plan.frequency.name}}
                        <template v-if="line.plan.frequency === 'monthly'">{{$t('cb.month')}}</template>
                        <template v-else-if="line.plan.frequency === 'annual'">{{$t('cb.year')}}</template>
                      </span>
                    </v-col>
                    <v-col cols="3" class="text-right">
                      <span v-if="line.plan.usage_scheme === 'licensed'" class="body-2 text--secondary font-weight-medium">{{$t('cb.qty')}} {{line.quantity}}</span>
                      <span v-else-if="line.plan.usage_scheme === 'metered'">{{$t('cb.useVaries')}}</span>
                    </v-col>
                    <v-col cols="3" class="text-right">
                      <template v-if="line.plan.usage_scheme === 'licensed'">
                        <span class="body-1 text--secondary font-weight-medium">{{(line.quantity * line.plan.amount) | currency(line.plan.currency.id)}}</span> <span class="text--primary">{{line.plan.currency.id}}</span>
                      </template>
                      <span v-else-if="line.plan.usage_scheme === 'metered'">{{$t('cb.useVaries')}}</span>
                    </v-col>
                  </v-row>
                  <template v-if="s.addons.length">
                    <v-row>
                      <v-col cols="12" class="pb-1">
                        <span class="subtitle-1 font-weight-medium">{{$t('cb.addons')}}</span>
                      </v-col>
                    </v-row>
                    <v-row class="py-3" no-gutters align="center" justify="space-between" v-for="(addon, i) in s.addons" :key="`${i}-addon`">
                      <v-col cols="6">
                        <span class="d-block mr-1 body-2 font-weight-medium">
                          {{addon.addon.name}}
                          <v-tooltip v-if="addon.rates.length" right max-width="280px" transition="scale-transition">
                            <template v-slot:activator="{ on }">
                              <v-icon class="ml-1" v-on="on" small :color="base.color_background">mdi-layers</v-icon>
                            </template>
                            <span>{{$t('cb.ratesMessage')}}</span>
                          </v-tooltip>
                        </span>
                        <span class="text--primary">{{addon.addon.amount | currency(addon.addon.currency.id)}} {{addon.addon.currency.id}} / <template v-if="addon.addon.apply_scheme === 'recurring'">{{$t('cb.recurring')}}</template><template v-else>{{$t('cb.oneTime')}}</template></span>
                      </v-col>
                      <v-col cols="3" class="text-right">
                        <span v-if="addon.addon.apply_scheme === 'recurring'">{{$tc('cb.remainingCycles', (addon.addon.duration_times - addon.addon_redemptions))}}</span>
                        <span v-else>{{$tc('cb.remainingCycles', (1 - addon.addon_redemptions))}}</span>
                      </v-col>
                      <v-col cols="3" class="text-right">
                        <span class="body-1 text--secondary font-weight-medium">{{(addon.quantity * addon.addon.amount) | currency(addon.addon.currency.id)}}</span> <span class="text--primary">{{addon.addon.currency.id}}</span>
                      </v-col>
                      <template v-if="addon.rates.length">
                        <v-col cols="12" class="pa-0">
                          <span class="d-block my-2" v-for="(rate, i) in addon.rates" :key="`${i}-rate`">{{$t('cb.rateCalendar', [rate.billing_cycle, rate.percentage, rate.description])}}</span>
                        </v-col>
                      </template>
                    </v-row>
                  </template>
                </v-card-text>

                <template v-if="sourcesList.length > 1 && base.manage_source">
                  <v-divider />
                  <v-card-text class="pa-0">
                    <v-list-item>
                      <v-list-item-title class="text-right body-2 text--secondary font-weight-medium">{{$t('cb.chargeToCard')}}</v-list-item-title>
                      <v-list-item-action>
                        <v-select v-if="editableSubscriptions[i]" v-model="editableSubscriptions[i].default_source" @change="patchSubscription(i)" :items="sourcesList" item-value="id" :loading="loaderUpdateSubscription" :menu-props="{ overflowY: true, transition: 'slide-y-transition' }" height="36" background-color="white" flat dense hide-details outlined placeholder="Usar tarjeta predeterminada" :ripple="false" single-line solo style="width:480px">
                          <template v-slot:item="data">
                            <v-row align="center" dense>
                              <img :src="require(`@/assets/appAsset-brand--${data.item.card.brand}.svg`)" :alt="data.item.card.brand" height="24" />
                              <span class="ml-2 body-2 text--secondary">•••• •••• •••• {{data.item.card.last4}}</span>
                            </v-row>
                          </template>
                          <template v-slot:selection="data">
                            <v-row align="center" justify="space-between">
                              <img class="ml-4" :src="require(`@/assets/appAsset-brand--${data.item.card.brand}.svg`)" :alt="data.item.card.brand" height="24" />
                              <span class="ml-2 body-2 text--secondary">•••• •••• •••• {{data.item.card.last4}}</span>
                            </v-row>
                          </template>
                        </v-select>
                      </v-list-item-action>
                    </v-list-item>
                  </v-card-text>
                </template>
              </v-card>
            </template>
          </v-card-text>
          <!-- end subscriptions -->

          <!-- sources -->
          <v-card-text>
            <v-row no-gutters>
              <v-col cols="12" class="pb-2">
                <span class="subtitle-1 font-weight-medium">{{$t('cb.cards')}}</span>
              </v-col>
            </v-row>
            <v-sheet outlined rounded width="100%">
              <template v-if="sourcesList.length">
                <v-list-item v-for="s in sourcesList" :key="s.id">
                  <v-list-item-icon :rounded="false">
                    <img :src="require(`@/assets/appAsset-brand--${s.card.brand}.svg`)" :alt="s.card.brand" height="24">
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      <span class="body-2 text--secondary font-weight-medium">•••• •••• •••• {{s.card.last4}}</span>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="base.manage_source && sourcesList.length > 1 && session.providers.length">
                    <v-btn @click="destroySource(s.id)" color="red darken-2" dark icon :loading="loaderDestroySource" :disabled="sourceInUse(s.id)" retain-focus-on-click><v-icon small>mdi-delete</v-icon></v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
              <v-row v-else>
                <v-col class="py-4 text-center">
                  <v-icon color="secondary" size="36">mdi-credit-card-off</v-icon>
                  <span class="d-block mt-2 body-2 text--primary">{{$t('cb.withoutCardsMessage')}}</span>
                </v-col>
              </v-row>
              <template v-if="base.manage_source && session.providers.length">
                <v-divider />
                <v-row class="px-7 py-3" justify="end">
                  <!-- add a new source menu -->
                  <v-menu :close-on-content-click="false" left min-width="400" max-width="400" :nudge-bottom="10" offset-y transition="slide-y-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn v-bind="attrs" v-on="on" :color="base.color_background" dark>{{$t('cb.addCard')}}</v-btn>
                    </template>
                    <v-card>
                      <v-card-title class="py-2">
                        <span class="text--secondary subtitle-1 font-weight-medium">{{$t('cb.addCard')}}</span>
                      </v-card-title>
                      <v-divider />
                      <v-card-text class="background">
                        <v-row align="center" justify="center" no-gutters>
                          <!-- none flow active provider gateway -->
                          <v-col cols="12" v-if="activeProvider.gateway.flow === 'none'">
                            <template v-if="activeProvider.gateway.id === 'stripe'">
                              <p-gateway-stripe ref="stripe" :test-mode="instance.test_mode" :gateway="activeProvider" />
                            </template>
                          </v-col>
                          <!-- none flow active provider gateway -->

                          <!-- redirect flow active provider gateway -->
                          <v-col cols="12" v-else-if="activeProvider.gateway.flow === 'redirect'">
                            <v-sheet class="my-4 pa-3" outlined rounded>
                              <v-radio-group dense v-model="selectedProvider">
                                <v-radio class="px-3" :value="true">
                                  <template v-slot:label>
                                    <v-row class="pl-3" align="center" justify="space-between">
                                      <img class="mt-1" :src="require(`@/assets/appAsset-brand--${activeProvider.gateway.id}.svg`)" :alt="activeProvider.gateway.id" height="32">
                                    </v-row>
                                  </template>
                                </v-radio>
                              </v-radio-group>
                            </v-sheet>
                            <v-alert class="mt-6 mb-4" text dense border="left" type="info" icon="mdi-information">{{$t('cb.addCardMessage')}}</v-alert>
                          </v-col>
                          <!-- end redirect flow active provider gateway -->
                        </v-row>
                      </v-card-text>
                      <v-divider />
                      <v-card-actions class="px-3">
                        <v-spacer />
                        <v-btn @click="createSource" :color="base.color_background" dark>{{$t('cb.continue')}}</v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-menu>
                  <!-- end add a new source menu -->
                </v-row>
              </template>
            </v-sheet>
          </v-card-text>

          <!-- invoices -->
          <v-card-text>
            <v-row no-gutters>
              <v-toolbar dense flat class="px-0">
                <v-toolbar-title class="ml-n4 subtitle-1 secondary--text font-weight-medium">{{$t('cb.invoices')}}</v-toolbar-title>
                <v-spacer />
                <v-pagination v-if="invoicesPageCount > 1" class="mr-n4" :color="base.color_background" :value="invoicesPage" :length="invoicesPageCount" :total-visible="7" @input="onInvoicesPageChange" />
              </v-toolbar>
              <v-col cols="12">
                <v-card class="mx-0" outlined>
                  <v-data-table :headers="headers" :items="invoicesList" :items-per-page="invoicesPageSize" :loading="loaderInvoices" mobile-breakpoint hide-default-footer hide-default-header disable-sort>
                    <template v-slot:no-data>
                      <v-row>
                        <v-col class="py-4 text-center">
                          <v-icon color="secondary" size="36">mdi-file</v-icon>
                          <span class="d-block mt-2 body-2 text--primary">{{$t('cb.withoutInvoicesMessage')}}</span>
                        </v-col>
                      </v-row>
                    </template>
                    <template v-slot:[`item.status`]="{item}">
                      <v-row align="center" justify="end">
                        <v-chip v-if="item.status === 'finalized' && item.due_date > today" class="mx-1" color="blue-grey darken-1" text-color="white" small>{{$t('cb.overdue')}}</v-chip>
                        <v-chip v-else-if="item.status === 'finalized' && item.due_date <= today" class="mx-1" color="deep-orange lighten-5" text-color="deep-orange accent-4" small>{{$t('cb.due')}}</v-chip>
                        <v-chip v-else-if="item.status === 'paid'" class="mx-1" color="blue lighten-5" text-color="blue accent-4" small>{{$t('cb.paid')}}</v-chip>
                        <v-chip v-else-if="item.status === 'cancelled'" class="mx-1" color="#E3E8EE" text-color="#525F7F" small>{{$t('cb.withCreditNote')}}</v-chip>
                      </v-row>
                    </template>
                    <template v-slot:[`item.document`]="{item}">
                      <v-list-item>
                        <v-list-item-title>
                          <span class="d-block mr-1 body-2">{{item.document.name}}</span>
                          <span class="d-block mr-1 body-2 text--secondary font-weight-medium" v-if="item.status !== 'draft'">Nº {{item.number}}</span>
                        </v-list-item-title>
                        <v-list-item-action>
                          <v-btn @click.prevent="downloadPdf(item)" :href="item.id" text :color="base.color_background" :ripple="false">PDF<v-icon class="mt-1" small right>mdi-arrow-down</v-icon></v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </template>
                    <template v-slot:[`item.due_date`]="{item}">
                      <span class="body-2 text--primary" v-if="item.due_date">{{item.due_date | date}}</span>
                      <span v-else>---</span>
                    </template>
                    <template v-slot:[`item.total`]="{item}">
                      <div class="text-right">
                        <span class="body-1 text--secondary font-weight-medium">{{item.total | currency(item.currency)}}</span>
                        <span class="ml-1 text--primary">{{item.currency}}</span>
                      </div>
                      <div v-if="item.total !== item.payments[0].balance" class="text-right">
                        <span class="mt-1 body-1 text--secondary font-weight-medium">{{item.payments[0].balance | currency(item.currency)}}</span>
                        <span class="ml-1 text--primary">{{item.currency}} por pagar</span>
                      </div>
                    </template>
                    <template v-slot:[`item.actions`]="{item}">
                      <div class="text-right">
                        <template v-if="item.status === 'finalized'">
                          <v-btn :href="`${$PAY_BASE}/payments/${getPaymentToPay(item.payments).id}`" target="_blank" text :color="base.color_background" :ripple="false">
                            {{$t('cb.pay')}}<v-icon small right>mdi-arrow-right</v-icon>
                          </v-btn>
                        </template>
                      </div>
                    </template>
                  </v-data-table>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <!-- end invoices -->

          <v-card-text>
            <v-row class="px-3" align="center" justify="end">
              <span class="caption text--primary">{{$t('cb.poweredByPiriodMessage')}}</span> <img class="ml-1" :src="require(`@/assets/appLogo-color--Onlight.svg`)" height="16">
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- end content -->
    </template>
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import { email, maxLength, required, requiredIf } from 'vuelidate/lib/validators'
import GenericViewMixin from '@/mixins/GenericViewMixin'
import WidgetViewMixin from '@/mixins/WidgetViewMixin'

export default {
  name: 'p-customer-base',
  mixins: [
    GenericViewMixin,
    WidgetViewMixin
  ],
  data: () => ({
    activeProvider: {
      gateway: {
        id: null,
        flow: 'none'
      },
      public_key: null
    },
    auth: {
      email: ''
    },
    authEmailSended: false,
    authError: '',
    billing: {
      address: '',
      country: 'CL',
      email: '',
      name: '',
      phone: '',
      state: null,
      tax_settings: {
        cl_activity_description: ''
      }
    },
    dialogBilling: false,
    editableSubscriptions: [],
    error: '',
    headers: [
      { value: 'document', text: 'Documento' },
      { value: 'status', text: '' },
      { value: 'due_date', text: 'Vencimiento', align: 'end', sortable: true },
      { value: 'total', text: 'Total', align: 'end', sortable: true },
      { value: 'actions', text: 'Total', align: 'end', sortable: true }
    ],
    loaderAuthEmail: false,
    loaderCancelSubscription: false,
    loaderDestroySource: false,
    loaderInvoices: false,
    loaderMain: false,
    loaderUpdateSubscription: false,
    options: {
      backgroundColor: {
        type: 'string',
        value: 'white'
      },
      locale: {
        type: 'string',
        value: 'es_MX'
      }
    },
    invoicesPage: 1,
    invoicesPageSize: 10,
    invoicesTotal: 0,
    selectedProvider: 'oneclick'
  }),
  computed: {
    ...mapState({
      base: state => state.customerBase.basesDetail,
      customer: state => state.customerBase.customersDetail,
      session: state => state.customerBase.sessionsDetail,
      invoicesList: state => state.customerBase.invoicesList,
      subscriptionsList: state => state.customerBase.subscriptionsList,
      sourcesList: state => state.customerBase.sourcesList,
      statesList: state => state.customerBase.statesList
    }),
    invoicesPageCount () {
      return Math.ceil(this.invoicesTotal / this.invoicesPageSize)
    }
  },
  created () {
    // check if is loaded in iframe
    if (window.self === window.top) {
      this.error = 'void'
      return false
    }

    /**
     * Initialize customer base authentication page
     */
    if (this.$route.name === 'CustomerBaseAuth') {
      this.$store.dispatch('customerBase/RETRIEVE', {
        resource: 'publishable/customer_base/bases',
        id: this.$route.params.pk
      })
      .then(() => {
        if (this.base.auth_scheme !== 'email') {
          this.error = 'not_allowed'
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          this.error = 'base_is_not_exist'
        }
      })
      .finally(() => {
        setTimeout(() => {
          this.setLanguaje()

          this.loaderMain = false
        }, 500)
      })
    }

    /**
     * Initialize customer base page
     */
    if (this.$route.name === 'CustomerBase') {
      this.loaderMain = true

      this.$store.dispatch('customerBase/RETRIEVE', {
        resource: 'publishable/customer_base/sessions',
        id: this.$route.params.session
      })
      .then(() => {
        if (!this.session.is_active) {
          this.error = 'session_expired'
        } else {
          Promise.all([
            this.retrieveBase(),
            this.retrieveCustomer(),
            this.retrieveSubscriptions(),
            this.retrieveInvoices(),
            this.listSources()
          ])
          .then(() => {
            this.setEditableSubscriptions()
          })
        }
      })
      .then(() => {
        const provider = this.session.providers[0]
        this.activeProvider.gateway.id = provider.gateway
        this.activeProvider.gateway.flow = 'redirect'
        this.activeProvider.public_key = provider.public_key
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          this.error = 'session_is_not_exist'
        }
      })
      .finally(() => {
        setTimeout(() => {
          this.setLanguaje()

          this.loaderMain = false
        }, 500)
      })
    }
  },
  methods: {
    async retrieveBase () {
      await this.$store.dispatch('customerBase/RETRIEVE', {
        resource: 'publishable/customer_base/bases',
        id: this.$route.params.pk
      })
    },
    async retrieveCustomer () {
      await this.$store.dispatch('customerBase/RETRIEVE', {
        resource: 'publishable/customer_base/customers',
        id: 'self',
        headers: {
          Authorization: `Session ${this.session.key}`
        }
      })
      .then(() => {
        Object.keys(this.billing).forEach((k) => {
          this.billing[k] = this.customer[k]
        })
      })
      .then(() => {
        if (this.base.manage_billing) {
          this.listStates()
        }
      })
    },
    async retrieveInvoices (page = this.invoicesPage) {
      this.loaderInvoices = true

      await this.$store.dispatch('customerBase/LIST', {
        resource: 'publishable/customer_base/invoices',
        params: {
          page_size: this.invoicesPageSize,
          page: page
        },
        headers: {
          Authorization: `Session ${this.session.key}`
        }
      })
      .then((response) => {
        this.invoicesPage = page
        this.invoicesTotal = response.data.count || 0
      })
      .finally(() => {
        this.loaderInvoices = false
      })
    },
    async retrieveSubscriptions () {
      await this.$store.dispatch('customerBase/LIST', {
        resource: 'publishable/customer_base/subscriptions',
        headers: {
          Authorization: `Session ${this.session.key}`
        }
      })
    },
    async listSources () {
      await this.$store.dispatch('customerBase/LIST', {
        resource: 'publishable/customer_base/sources',
        headers: {
          Authorization: `Session ${this.session.key}`
        }
      })
    },
    cancelSubscription (subscription) {
      this.loaderCancelSubscription = true

      this.$store.dispatch('customerBase/RETRIEVE', {
        resource: 'publishable/customer_base/subscriptions',
        detail: 'cancel',
        id: subscription.id,
        query: {
          base: this.base.publishable_key
        },
        headers: {
          Authorization: `Session ${this.session.key}`
        }
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
      .finally(() => {
        this.loaderCancelSubscription = false
      })
    },
    createSource () {
      const payload = {
        gateway: this.activeProvider.gateway.id
      }

      if (this.$route.query.referrer) {
        payload.return_url = this.$route.query.referrer
      }

      this.$store.dispatch('customerBase/CREATE', {
        resource: 'publishable/customer_base/sources',
        payload: payload,
        headers: {
          Authorization: `Session ${this.session.key}`
        },
        setCommit: false,
        setLoader: false
      })
      .then((response) => {
        this.trySourceInscription(response.data.id)
      })
    },
    destroySource (id) {
      this.$dialog.warning({
        title: this.$t('cb.removeCard'),
        text: `
          <span class="d-block mb-4">${this.$t('cb.removeCardMessage')}</span>
          <span class="d-block mb-4">${this.$t('cb.continueWarningMessage')}</span>
          <span class="d-block font-weight-medium">${this.$t('cb.undoneMessage')}</span>
        `,
        actions: [
          {
            color: 'red',
            text: this.$t('cb.confirm'),
            handle: () => {
              this.loaderDestroySource = true

              this.$store.dispatch('customerBase/DESTROY', {
                resource: 'publishable/customer_base/sources',
                id: id,
                headers: {
                  Authorization: `Session ${this.session.key}`
                }
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
              .finally(() => {
                this.loaderDestroySource = false
              })
            }
          }
        ]
      })
    },
    downloadPdf (instance) {
      if (this.renderUrl) {
        return false
      }

      this.$store.dispatch('customerBase/RETRIEVE', {
        resource: 'publishable/customer_base/invoices',
        detail: 'pdf',
        id: instance.number,
        setDetail: false,
        loader: false,
        headers: {
          Authorization: `Session ${this.session.key}`
        }
      })
      .then((response) => {
        const byteCharacters = atob(response.data.file)
        const byteNumbers = new Array(byteCharacters)

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }

        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], {type: 'application/pdf'})
        const link = document.createElement('a')

        link.href = URL.createObjectURL(blob)
        link.download = `${instance.document.id}_N${instance.number}`
        link.click()

        URL.revokeObjectURL(link.href)
      })
    },
    getPaymentToPay (payments) {
      return payments.filter(payment => !['succeeded', 'canceled'].includes(payment.status))[0]
    },
    listStates () {
      this.$store.dispatch('customerBase/LIST', {
        resource: 'publishable/customer_base/states',
        params: {
          country: this.customer.country,
          page_size: 500
        },
        headers: {
          Authorization: `Session ${this.session.key}`
        }
      })
    },
    onRedirect (url) {
      /**
       * Post message to widget library redirect to value event
       */
      parent.postMessage({
        event: 'widget_redirect',
        value: url
      }, '*')
    },
    patchCustomer () {
      this.$v.billing.$touch()

      if (this.$v.billing.$invalid) {
        return false
      }

      const payload = {
        address: this.billing.address,
        email: this.billing.email,
        name: this.billing.name,
        phone: this.billing.phone,
        state: this.billing.state,
        tax_settings: this.billing.tax_settings
      }

      this.$store.dispatch('customerBase/UPDATE', {
        resource: 'publishable/customer_base/customers',
        id: 'self',
        payload: payload,
        headers: {
          Authorization: `Session ${this.session.key}`
        },
        setDetail: false,
        loader: false
      })
      .then(() => {
        this.dialogBilling = false
      })
      .catch(() => {
        this.$dialog.message.error(this.$t('cb.errorUpdateBillingInformation'))
      })
    },
    patchSubscription (i) {
      const subscription = this.editableSubscriptions[i]

      this.loaderUpdateSubscription = true

      this.$store.dispatch('customerBase/UPDATE', {
        resource: 'publishable/customer_base/subscriptions',
        id: subscription.id,
        payload: {
          default_source: subscription.default_source
        },
        headers: {
          Authorization: `Session ${this.session.key}`
        },
        setDetail: false,
        loader: false
      })
      .catch(() => {
        this.$dialog.message.error(this.$t('cb.errorUpdateSubscription'))
      })
      .finally(() => {
        this.loaderUpdateSubscription = false
      })
    },
    trySourceInscription (sourceId) {
      this.onRedirect(`${process.env.VUE_APP_API_BASE}/sources/${sourceId}/authorize/`)
    },
    setEditableSubscriptions () {
      this.editableSubscriptions = this.subscriptionsList.map(s => {
        return { id: s.id, default_source: s.default_source }
      })
    },
    setLanguaje () {
      this.$v.options.locale.value.$touch()

      if (this.$v.options.locale.value.$invalid) {
        console.info('Unsupported value in piriodSettings.options.locale, must be one of [es_MX, en_US, po_BR]')
      } else {
        this.$i18n.locale = this.options.locale.value
      }
    },
    signin () {
      this.$v.auth.$touch()
      if (this.$v.auth.$invalid) {
        return false
      }

      const payload = {
        base: this.$route.params.pk,
        email: this.auth.email
      }
      if (this.$route.query.referrer) {
        payload.referrer = this.$route.query.referrer
      }

      this.loaderAuthEmail = true

      this.$store.dispatch('apps/CREATE', {
        resource: 'publishable/customer_base/emailsessions',
        payload: payload
      })
      .then(() => {
        this.authEmailSended = true
        this.authError = ''
      })
      .catch((error) => {
        this.authError = error.response.data.non_field_errors
      })
      .finally(() => {
        this.loaderAuthEmail = false
      })
    },
    sourceInUse (id) {
      const usedSources = this.editableSubscriptions.map(s => s.default_source)
      return usedSources.includes(id)
    },
    onInvoicesPageChange (page) {
      if (page === this.invoicesPage || this.loaderInvoices) {
        return false
      }

      this.retrieveInvoices(page)
    }
  },
  validations: {
    auth: {
      email: {
        email,
        required
      }
    },
    billing: {
      address: {
        required,
        maxLength: maxLength(64)
      },
      email: {
        required,
        email,
        maxLength: maxLength(254)
      },
      name: {
        required,
        maxLength: maxLength(100)
      },
      phone: {
        maxLength: maxLength(16)
      },
      state: {
        required
      },
      tax_settings: {
        cl_activity_description: {
          required: requiredIf(function () {
            return this.billing.country === 'CL'
          }),
          maxLength: maxLength(64)
        }
      }
    },
    options: {
      locale: {
        value: {
          in: function (v) {
            return ['es_MX', 'en_US', 'po_BR'].includes(v)
          }
        }
      }
    }
  }
}
</script>

<style>
  .v-dialog__content {
    align-items: start !important;
    justify-content: center !important;
    top: 80px !important;
  }
</style>
